import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Check, Search } from 'lucide-react';
import Input from '../Input';
import Button from '../Button';
import ColorSwatch from '../ColorSwatch';

/** Above this many options a list gets a search field (unless `searchable` says otherwise). */
export const SEARCH_THRESHOLD = 8;

const fold = (s) => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * FilterOptionList — the option rows of a Filter attribute (internal), in the
 * DS Input dropdown look: --input-option-* rows with a hairline, gray-05
 * hover, the selected background and the 16px check box of the multiselect.
 *
 * Every row is a real <input type="checkbox|radio"> inside its <label>, so
 * Tab / Space / arrow keys and screen readers work natively.
 * - multiple  → checkboxes, toggled on change
 * - !multiple → radios with a tick on the picked row; clicking the picked row
 *               again clears it (deselectable) or is ignored
 * An option whose count is 0 is disabled — unless it is picked, so it can be
 * unticked. `limit` folds a long list to that many rows + "Show all (N)".
 */
const FilterOptionList = ({
  name,
  legend,
  options,
  selected = [],
  multiple = true,
  deselectable = true,
  onToggle,
  counts,
  searchable,
  limit,
  labels,
}) => {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(
    () => !limit || options.slice(limit).some((o) => selected.includes(o.value)),
  );

  const showSearch = searchable ?? options.length > SEARCH_THRESHOLD;
  const q = fold(query.trim());
  const matches = q ? options.filter((o) => fold(o.label).includes(q)) : options;
  const foldable = Boolean(limit) && !q && matches.length > limit;
  const visible = foldable && !expanded ? matches.slice(0, limit) : matches;

  return (
    <div className="ds-filter-options">
      {showSearch && (
        <div className="ds-filter-options__search">
          <Input
            type="text"
            label={labels.search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            icon={<Search size={20} />}
            showClear
            autoComplete="off"
          />
        </div>
      )}

      <div className="ds-filter-options__list" role="group" aria-label={legend}>
        {visible.map((option) => {
          const isSelected = selected.includes(option.value);
          const count = counts ? counts[option.value] : option.count;
          const disabled = Boolean(option.disabled) || (count === 0 && !isSelected);
          const rowClass = [
            'ds-filter-option',
            isSelected && 'ds-filter-option--selected',
            disabled && 'ds-filter-option--disabled',
          ].filter(Boolean).join(' ');

          const inputProps = multiple
            ? { type: 'checkbox', onChange: () => onToggle(option.value) }
            : {
              type: 'radio',
              // A radio never fires change when it is already picked — the
              // click does, so the picked row can clear (or confirm) itself.
              onChange: () => {},
              onClick: () => {
                if (!isSelected || deselectable) onToggle(option.value);
                else onToggle(option.value, { repeat: true });
              },
            };

          return (
            <label key={option.value} className={rowClass}>
              <input
                {...inputProps}
                className="ds-filter-option__input"
                name={name}
                value={option.value}
                checked={isSelected}
                disabled={disabled}
              />
              {multiple && (
                <span className="ds-filter-option__box" aria-hidden="true">
                  {isSelected && <Check size={14} strokeWidth={2.5} />}
                </span>
              )}
              {option.swatch && <ColorSwatch color={option.swatch} size="l" className="ds-filter-option__swatch" />}
              <span className="ds-filter-option__label">{option.label}</span>
              {typeof count === 'number' && (
                <span className="ds-filter-option__count typo-body-sm">{count}</span>
              )}
              {!multiple && isSelected && (
                <Check size={16} className="ds-filter-option__tick" aria-hidden="true" />
              )}
            </label>
          );
        })}
      </div>

      {visible.length === 0 && <p className="ds-filter-options__empty typo-body">{labels.noMatches}</p>}

      {foldable && (
        <div className="ds-filter-options__more">
          <Button size="tertiary" variant="text" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
            {expanded ? labels.showLess : labels.showAll(matches.length)}
          </Button>
        </div>
      )}
    </div>
  );
};

FilterOptionList.propTypes = {
  /** Input name — groups the radios */
  name: PropTypes.string.isRequired,
  /** Accessible name of the option group */
  legend: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    swatch: PropTypes.string,
    count: PropTypes.number,
    disabled: PropTypes.bool,
  })).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string),
  multiple: PropTypes.bool,
  deselectable: PropTypes.bool,
  /** (value, { repeat }) — repeat: the picked radio was pressed again (not deselectable) */
  onToggle: PropTypes.func.isRequired,
  /** { [optionValue]: count } — overrides option.count */
  counts: PropTypes.objectOf(PropTypes.number),
  /** Force the search field on / off; default: more than SEARCH_THRESHOLD options */
  searchable: PropTypes.bool,
  /** Fold to this many rows with a "Show all" toggle (the drawer) */
  limit: PropTypes.number,
  labels: PropTypes.object.isRequired,
};

export default FilterOptionList;
